import { Table } from "antd";
import { cloneDeep } from "lodash-es";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { useFetchSeries } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";
import AddNewSeriesModal from "./AddNewSeriesModal";
import useGetSeriesListColumns from "./useGetSeriesListColumns";

const PermissionName = PermissionNames.SEASON_SERIES;

const sortSeries = ({ res, seasonId }) => {
  let mergedData = cloneDeep(res?.data);
  mergedData = mergedData?.map((series) => {
    if (series?.stageCode) {
      return {
        ...series,
        stageName: res?.includes?.resources?.seasonStages?.[`${seasonId}:${series?.stageCode}`]?.nameLocal || null,
        stageOrder: res?.includes?.resources?.seasonStages?.[`${seasonId}:${series?.stageCode}`]?.stageOrder || null,
      };
    }
    return series;
  });
  // sort by stageOrder
  const withStage = mergedData?.filter((series) => series?.stageCode)?.sort((a, b) => a?.stageOrder - b?.stageOrder);
  const noStage = mergedData?.filter((series) => !series?.stageCode);
  // merge both
  return [...withStage, ...noStage];
};

const SeriesList = ({ reloadSeriesRef }) => {
  const { seasonId } = useParams();
  const [seriesData, setSeriesData] = useState([]);
  const [includedData, setIncludedData] = useState([]);
  const [stages, setStages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const { data, isLoading } = useFetchSeries({
    limit: pageSize,
    offset,
    include: "seasonStages,entities",
  });

  const nextLink = useMemo(() => data?.links?.next, [data?.links?.next]);

  useEffect(() => {
    if (data) {
      setSeriesData(sortSeries({ res: data, seasonId }));
      setIncludedData(data?.includes?.resources);
      if (data?.includes?.resources?.seasonStages) {
        setStages(Object.values(data.includes.resources.seasonStages));
      }
    }
  }, [data, seasonId]);

  const columns = useGetSeriesListColumns({
    seriesData: seriesData,
    stages: stages,
    fetchSeriesList: () => {},
    includedData: includedData,
  });

  useEffect(() => {
    setFiltered(seriesData);
  }, [seriesData]);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "end" }} className={"mb-2"}>
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
          <AddNewSeriesModal reloadSeriesRef={reloadSeriesRef} />
        </PermissionCheck>
      </div>
      <Table
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + seriesData.length + 1 : seriesData.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
        loading={isLoading}
        size={"middle"}
        columns={columns}
        dataSource={filtered}
        rowKey={"seriesCode"}
      />
    </>
  );
};

export default SeriesList;
